
import { Component, State, Vue, Watch } from 'nuxt-property-decorator';
import type { Route } from 'vue-router';
import SearchMenu from '../search/SearchMenu.vue';
import TooltipButton from '../TooltipButton.vue';
import CloseBtn from '../side-panel/CloseBtn.vue';
import GlobalMessage from '../toast/GlobalMessage.vue';
import UserMenu from './UserMenu.vue';
import Logo from '@/components/Logo.vue';
import ThemeStore from '@/store/theme.store';
import { Metadata, Page, RootPage, Sso } from '@/types/pageContract';
import ToolsMenu from '@/components/ui/layout/ToolsMenu.vue';
import { SidePanelMode } from '@/components/ui/side-panel/SidePanelMode';
import { constructTYPO3Url, getSignInUrl } from '@/util/typo3Urls';
import { Navigation, Settings, Site, User } from '@/types/initialData';
import SidePanelStore from '@/components/ui/side-panel/side-panel.store';
import LocalConfigStore from '@/store/localConfig.store';
import { nrPageAction } from '@/util/newrelic-browser';

@Component({
  components: {
    GlobalMessage,
    CloseBtn,
    TooltipButton,
    UserMenu,
    ToolsMenu,
    SearchMenu,
    Logo
  }
})
export default class AppBar extends Vue {
  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.user?.info?.id)
  isLoggedIn: boolean;

  @State((state) => state.typo3?.initial?.site?.user)
  user: User;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.sso)
  sso: Sso;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.navigation?.[0] ?? [])
  navigation!: Navigation;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.metadata)
  metadata!: Metadata;

  // @ts-ignore
  @State((state) => state.typo3?.page?.page)
  page: Page;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site)
  site: Site;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.settings)
  settings!: Settings;

  // @ts-ignore
  @State((state) => state.typo3?.page)
  rootPage!: RootPage;

  gradientAngle: string = '270';

  get rightPanelOpenInSearchMode(): boolean {
    return (
      SidePanelStore.mode === SidePanelMode.SEARCH &&
      SidePanelStore.rightPanelIsOpen
    );
  }

  get mainLink(): string {
    if (this.navigation && this.navigation.uid) {
      return constructTYPO3Url(`${this.navigation.uid}`);
    }

    return '';
  }

  get headerHeight(): string {
    const desktopHeight = '64';
    const mobileHeight = '70';

    if (this.$vuetify.breakpoint.mdAndUp) {
      return desktopHeight;
    }

    return mobileHeight;
  }

  get headerHeightScrolled(): string {
    const desktopHeight = '64';
    const mobileHeight = '48';

    if (this.$vuetify.breakpoint.mdAndUp) {
      return desktopHeight;
    }

    return mobileHeight;
  }

  get showDevTools(): boolean {
    return process.env.NODE_ENV !== 'production';
  }

  get handleThemingColors(): string {
    return this.$vuetify.theme.dark && this.appIsScrolled
      ? 'black--text'
      : this.$vuetify.theme.dark
      ? 'white--text'
      : this.appIsScrolled || this.isBright
      ? 'black--text'
      : 'white--text';
  }

  get appBarHeight() {
    return {
      height: this.appIsScrolled
        ? `${this.headerHeightScrolled}px`
        : `${this.headerHeight}px`
    };
  }

  get isBright(): boolean {
    return ThemeStore.isBright;
  }

  get appIsScrolled(): boolean {
    return ThemeStore.appIsScrolled;
  }

  get metaDataTitle(): string {
    return this.metadata ? this.metadata.title : '';
  }

  get gradientProps() {
    return {
      '--gradientAngle': `${this.gradientAngle}deg`
    };
  }

  get signInUrl(): string {
    return LocalConfigStore.signInUrl;
  }

  @Watch('$route', { immediate: true })
  onRouteChanged(_newRoute: Route): void {
    LocalConfigStore.signInUrl =
      this.sso.url_account_home && this.sso.url_signin
        ? getSignInUrl(this.sso.url_signin)
        : '#';
  }

  // We use this to handle restricted users
  get isIpAccess(): boolean {
    return LocalConfigStore.isIpAccess;
  }

  toggleSearchPanel(): void {
    SidePanelStore.mode = SidePanelMode.SEARCH;
    SidePanelStore.rightPanelIsOpen = !SidePanelStore.rightPanelIsOpen;
  }

  openDevelopmentTools(): void {
    if (
      SidePanelStore.rightPanelIsOpen &&
      SidePanelStore.mode === SidePanelMode.DEVELOPMENT
    ) {
      SidePanelStore.rightPanelIsOpen = false;
    } else {
      SidePanelStore.rightPanelIsOpen = true;
      SidePanelStore.mode = SidePanelMode.DEVELOPMENT;
    }
  }

  proxyPageAction(name: string): void {
    nrPageAction(name, {
      type: 'Button',
      component: 'AppBar'
    });
  }
}
